import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PageHeader, Loading } from "@/components/common";
import { gbp, num } from "@/lib/format";
import { Table2, Warehouse, CalendarDays, PiggyBank, Receipt, ArrowRight, TrendingUp, Package } from "lucide-react";

const CARDS = [
  { key: "rate_cards", label: "Rate Cards", icon: Table2, testid: "metric-rate-cards-count", to: "/rate-cards", fmt: (v) => num(v), tint: "bg-blue-50 text-blue-600" },
  { key: "products", label: "Products", icon: Package, testid: "metric-products-count", to: "/products", fmt: (v) => num(v), tint: "bg-cyan-50 text-cyan-600" },
  { key: "depots", label: "Active Depots", icon: Warehouse, testid: "metric-active-depots", to: "/sites", fmt: (v) => num(v), tint: "bg-indigo-50 text-indigo-600" },
  { key: "calendar_weeks", label: "Calendar Weeks (FY27-31)", icon: CalendarDays, testid: "metric-calendar-weeks", to: "/calendar", fmt: (v) => num(v), tint: "bg-violet-50 text-violet-600" },
  { key: "total_savings", label: "Total Savings", icon: PiggyBank, testid: "metric-total-savings", to: "/savings", fmt: (v) => gbp(v), tint: "bg-emerald-50 text-emerald-600" },
  { key: "total_cogs", label: "Cost of Goods", icon: Receipt, testid: "metric-total-cogs", to: "/cost-of-goods", fmt: (v) => gbp(v), tint: "bg-amber-50 text-amber-600" },
  { key: "total_credits", label: "Service Credits Due", icon: TrendingUp, testid: "metric-total-credits", to: "/savings", fmt: (v) => gbp(v), tint: "bg-rose-50 text-rose-600" },
];

export default function DashboardOverview() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/stats").then((r) => r.data) });

  if (isLoading) return <Loading />;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Overview of your rate cards, calendar and financial records." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <Card
              key={c.key}
              data-testid={c.testid}
              onClick={() => navigate(c.to)}
              className="p-5 cursor-pointer group hover:shadow-md hover:-translate-y-0.5 transition-all border-slate-200 dark:border-slate-800"
            >
              <div className="flex items-start justify-between">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.tint}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
              </div>
              <p className="mt-4 text-3xl font-display font-bold text-slate-900 dark:text-white font-mono-num">
                {c.fmt(data?.[c.key])}
              </p>
              <p className="text-sm text-slate-500 mt-1">{c.label}</p>
            </Card>
          );
        })}
      </div>

      <Card className="mt-6 p-6 border-slate-200 dark:border-slate-800 bg-gradient-to-br from-slate-900 to-blue-950 text-white">
        <h3 className="font-display font-semibold text-lg">Financial Calendar {data?.fy_range}</h3>
        <p className="text-sm text-slate-300 mt-1 max-w-2xl">
          The 4-4-5 period calendar is generated automatically from FY27 (commencing Monday 26-10-2026) through FY31,
          with week commencing / week ending dates, periods and quarters. All dates display in DD-MM-YYYY format.
        </p>
        <button
          onClick={() => navigate("/calendar")}
          data-testid="dashboard-view-calendar-button"
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium bg-white/10 hover:bg-white/20 rounded-lg px-4 py-2 transition-colors"
        >
          View calendar <ArrowRight className="w-4 h-4" />
        </button>
      </Card>
    </div>
  );
}
